import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Flex, Text, Badge, ActionIcon, Tooltip, Skeleton, Button, Group } from "@mantine/core";
import {
  IconChevronLeft,
  IconChevronRight,
  IconRefresh,
  IconTrophy,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import { LEAGUE } from "../../models/NBA";
import { getScheduleGames, getLiveScores } from "../../api/Info";
import { ScheduleGame, ScheduleGameTeam, SeasonStage } from "../../client";
import classes from "./ScheduleGames.module.css";

interface ScheduleGamesProps {
  league: LEAGUE;
}

interface DayGroup {
  dateKey: string;
  dayName: string;
  dateLabel: string;
  isToday: boolean;
  games: ScheduleGame[];
}

const STAGE_CONFIG: Record<SeasonStage, { color: string; label: string }> = {
  "Preseason": { color: "orange", label: "Preseason" },
  "Regular Season": { color: "gray", label: "Reg Season" },
  "Commissioner's Cup": { color: "grape", label: "Comm. Cup" },
  "Emirates NBA Cup": { color: "indigo", label: "NBA Cup" },
  "Playoffs": { color: "teal", label: "Playoffs" },
  "Finals": { color: "yellow", label: "Finals" },
  "Play-In": { color: "cyan", label: "Play-In" },
  "All-Star": { color: "pink", label: "All-Star" },
};

const formatDisplayTime = (game: ScheduleGame): string => {
  if (game.gameStatusText === "TBD") {
    return "TBD";
  }
  if (game.gameStatusText && (game.gameStatusText.toLowerCase().includes("pm") || game.gameStatusText.toLowerCase().includes("am"))) {
    return game.gameStatusText;
  }
  if (game.gameTimeEst && (game.gameTimeEst.toLowerCase().includes("pm") || game.gameTimeEst.toLowerCase().includes("am"))) {
    return game.gameTimeEst;
  }
  return game.gameStatusText || "TBD";
};

const groupGamesByDay = (gamesList: ScheduleGame[]): DayGroup[] => {
  const groups: Record<string, DayGroup> = {};
  const todayStr = dayjs().format("YYYY-MM-DD");
  const tomorrowStr = dayjs().add(1, "day").format("YYYY-MM-DD");

  for (const game of gamesList) {
    const rawDateStr = game.gameDateEst ? game.gameDateEst.slice(0, 10) : "";
    const dateKey = rawDateStr || dayjs(game.date).format("YYYY-MM-DD");

    if (!groups[dateKey]) {
      const d = dayjs(dateKey);
      const isToday = dateKey === todayStr;
      const isTomorrow = dateKey === tomorrowStr;
      const dayName = isToday ? "Today" : isTomorrow ? "Tomorrow" : d.format("ddd");
      const dateLabel = d.format("MMM D");
      groups[dateKey] = {
        dateKey,
        dayName,
        dateLabel,
        isToday,
        games: [],
      };
    }
    groups[dateKey].games.push(game);
  }

  return Object.values(groups);
};

const getTeamDisplayName = (team: ScheduleGameTeam): string => {
  if (team.isTbd || !team.id || team.id === "0" || team.abbv === "TBD") {
    return "TBD"
  }
  const fullName = [team.city, team.name].filter(Boolean).join(" ")
  return fullName || team.abbv || "TBD"
}

const MiniTeamRow: React.FC<{ team: ScheduleGameTeam; league: LEAGUE; isLiveOrFinal: boolean }> = ({
  team,
  league,
  isLiveOrFinal,
}) => {
  const [imgError, setImgError] = useState<boolean>(false)

  useEffect(() => {
    setImgError(false)
  }, [team.id, league])

  const isTbd = Boolean(team.isTbd || !team.id || team.id === "0" || team.abbv === "TBD")
  const logoUrl = !isTbd ? `https://cdn.${league}.com/logos/${league}/${team.id}/primary/L/logo.svg` : ""
  const displayTricode = isTbd ? "TBD" : team.abbv || "TBD"

  return (
    <div className={classes.teamRow}>
      <div className={classes.teamLeft}>
        {isTbd ? (
          <div className={classes.miniPlaceholderLogo}>
            <IconTrophy size={10} color="#fcc419" />
          </div>
        ) : !imgError ? (
          <img
            src={logoUrl}
            alt={team.abbv || "Team"}
            className={classes.miniTeamLogo}
            onError={() => setImgError(true)}
          />
        ) : (
          <Text fz="10px" fw={700} c="dimmed">
            {team.abbv?.slice(0, 3)}
          </Text>
        )}
        <Text
          className={classes.miniTricode}
          title={displayTricode}
        >
          {displayTricode}
        </Text>
      </div>

      {isLiveOrFinal ? (
        <Text className={classes.miniScore} c={typeof team.score === "number" ? "#ffffff" : "dimmed"}>
          {typeof team.score === "number" ? team.score : "-"}
        </Text>
      ) : !isTbd &&
        typeof team.wins === "number" &&
        typeof team.losses === "number" &&
        (team.wins > 0 || team.losses > 0) ? (
        <Text className={classes.miniRecord}>
          {team.wins}-{team.losses}
        </Text>
      ) : null}
    </div>
  );
};

export const ScheduleGames: React.FC<ScheduleGamesProps> = ({ league }) => {
  const [games, setGames] = useState<ScheduleGame[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const scrollRef = useRef<HTMLDivElement | null>(null)
 
  useEffect(() => {
    setGames([])
    setLoading(true)
    setError(false)
  }, [league])

  const fetchGames = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      setError(false);
      try {
        const data = await getScheduleGames(league, 5, 25, signal);
        if (signal?.aborted) return;
        setGames(data);
      } catch (err: unknown) {
        if ((err as Error)?.name === "AbortError" || signal?.aborted) return;
        setError(true);
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    [league]
  );

  useEffect(() => {
    const controller = new AbortController()
    fetchGames(controller.signal)
    return () => controller.abort()
  }, [fetchGames])

  const hasLiveGames = games.some((g) => g.isLive);

  const pollLiveScores = useCallback(async () => {
    try {
      const liveScores = await getLiveScores(league);
      if (!liveScores || liveScores.length === 0) return;
      setGames((prevGames) =>
        prevGames.map((game) => {
          const update = liveScores.find((ls) => ls.id === game.id);
          if (!update) return game;
          return {
            ...game,
            gameStatus: update.gameStatus,
            gameStatusText: update.gameStatusText,
            period: update.period,
            gameClock: update.gameClock,
            isLive: update.isLive,
            lastPlay: update.lastPlay ?? game.lastPlay,
            homeTeam: {
              ...game.homeTeam,
              score: typeof update.homeScore === "number" ? update.homeScore : game.homeTeam.score,
            },
            awayTeam: {
              ...game.awayTeam,
              score: typeof update.awayScore === "number" ? update.awayScore : game.awayTeam.score,
            },
          };
        })
      );
    } catch {
      // Non-fatal background poll error
    }
  }, [league]);

  useEffect(() => {
    if (!hasLiveGames) return;
    const interval = setInterval(() => {
      pollLiveScores();
    }, 30000);
    return () => clearInterval(interval);
  }, [hasLiveGames, pollLiveScores]);

  const handleScroll = (direction: "left" | "right") => {
    if (!scrollRef.current) return;
    const scrollAmount = 260;
    scrollRef.current.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  const handleCardClick = (game: ScheduleGame) => {
    const targetUrl = game.gameUrl || `https://www.${league}.com/game/${game.id}`;
    window.open(targetUrl, "_blank", "noopener,noreferrer");
  };

  const dayGroups = groupGamesByDay(games);

  return (
    <Box className={classes.container} mb={12}>
      <Flex justify="space-between" align="center" px={{ base: "xs", sm: "md" }} mb={4}>
        <Flex align="center" gap="xs">
          <Text fz="11px" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: "0.8px" }}>
            Schedule & Live Games
          </Text>
          {hasLiveGames && (
            <Badge color="red" variant="filled" size="xs" className={classes.liveBadge}>
              ● LIVE
            </Badge>
          )}
        </Flex>

        <Group gap={4}>
          <Tooltip label="Refresh schedule" withArrow position="bottom">
            <ActionIcon
              variant="subtle"
              color="gray"
              size="xs"
              radius="sm"
              onClick={() => fetchGames()}
              aria-label="Refresh schedule"
            >
              <IconRefresh size={12} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Scroll left" withArrow position="bottom">
            <ActionIcon
              variant="subtle"
              color="gray"
              size="xs"
              radius="sm"
              onClick={() => handleScroll("left")}
              aria-label="Scroll left"
            >
              <IconChevronLeft size={13} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Scroll right" withArrow position="bottom">
            <ActionIcon
              variant="subtle"
              color="gray"
              size="xs"
              radius="sm"
              onClick={() => handleScroll("right")}
              aria-label="Scroll right"
            >
              <IconChevronRight size={13} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Flex>

      {loading && games.length === 0 ? (
        <Flex gap="xs" px={{ base: "xs", sm: "md" }} style={{ overflowX: "hidden" }}>
          {Array.from({ length: 6 }).map((_, idx) => (
            <Skeleton key={idx} height={68} miw={150} radius="sm" />
          ))}
        </Flex>
      ) : error ? (
        <Box px={{ base: "xs", sm: "md" }} py="xs" ta="center">
          <Text c="dimmed" fz="xs" mb={4}>
            Failed to load games.
          </Text>
          <Button size="compact-xs" variant="light" color="blue" onClick={() => fetchGames()}>
            Retry
          </Button>
        </Box>
      ) : games.length === 0 ? (
        <Box px={{ base: "xs", sm: "md" }} py="xs" ta="center">
          <Text c="dimmed" fz="xs">No games scheduled in the next 5 days.</Text>
        </Box>
      ) : (
        <Box className={classes.scrollArea} ref={scrollRef} px={{ base: "xs", sm: "md" }}>
          <div className={classes.cardsWrapper}>
            {dayGroups.map((group) => (
              <React.Fragment key={group.dateKey}>
                <div className={classes.daySeparator}>
                  <div className={classes.daySeparatorLine} />
                  <div className={`${classes.daySeparatorBadge} ${group.isToday ? classes.daySeparatorToday : ""}`}>
                    <Text className={classes.daySeparatorDay}>{group.dayName}</Text>
                    <Text className={classes.daySeparatorDate}>{group.dateLabel}</Text>
                  </div>
                  <div className={classes.daySeparatorLine} />
                </div>

                {group.games.map((game) => {
                  const isLive = game.isLive;
                  const isFinal = game.gameStatus === 3;
                  const isLiveOrFinal = isLive || isFinal;
                  const stageConfig = (game.seasonStage && STAGE_CONFIG[game.seasonStage]) || STAGE_CONFIG["Regular Season"];
                  const displayTime = formatDisplayTime(game);
                  const awayName = getTeamDisplayName(game.awayTeam)
                  const homeName = getTeamDisplayName(game.homeTeam)
                  const tooltipLabel = isLive && game.lastPlay
                    ? `${awayName} @ ${homeName} • Last play: ${game.lastPlay}`
                    : `${awayName} @ ${homeName}`;

                  return (
                    <Tooltip
                      key={game.id}
                      label={tooltipLabel}
                      withArrow
                      position="bottom"
                    >
                      <div
                        className={`${classes.miniGameBox} ${isLive ? classes.miniGameBoxLive : ""}`}
                        onClick={() => handleCardClick(game)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            handleCardClick(game);
                          }
                        }}
                      >
                        <div className={classes.microHeader}>
                          {isLive ? (
                            <Flex align="center" gap={4}>
                              <Badge color="red" variant="filled" size="9px" px={4} py={1} className={classes.liveBadge}>
                                LIVE
                              </Badge>
                              <Text fz="10px" fw={800} c="red.4">
                                {game.period ? `Q${game.period}` : ""} {game.gameClock || ""}
                              </Text>
                            </Flex>
                          ) : isFinal ? (
                            <Text fz="10px" fw={700} c="dimmed">
                              FINAL
                            </Text>
                          ) : (
                            <Text fz="10px" fw={600} c="dimmed">
                              {displayTime}
                            </Text>
                          )}

                          <Badge
                            size="9px"
                            px={4}
                            py={1}
                            variant="light"
                            color={stageConfig.color}
                            radius="xs"
                            style={{ textTransform: "none", fontWeight: 700 }}
                          >
                            {stageConfig.label}
                          </Badge>
                        </div>

                        <MiniTeamRow team={game.awayTeam} league={league} isLiveOrFinal={isLiveOrFinal} />
                        <MiniTeamRow team={game.homeTeam} league={league} isLiveOrFinal={isLiveOrFinal} />
                      </div>
                    </Tooltip>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </Box>
      )}
    </Box>
  );
};

export default ScheduleGames;
